'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import VisaApplicationCard from './VisaApplicationCard';
import VisaStatusTimeline from './VisaStatusTimeline';
import UrgencyAnalysis from './UrgencyAnalysis';
import MarketplaceRecommendations from './MarketplaceRecommendations';
import StatusAlerts from './StatusAlerts';

interface VisaApplication {
  id: string;
  applicationType: string;
  destination: string;
  nationality: string;
  applicationNumber: string;
  submittedDate: string;
  currentStatus: 'submitted' | 'under-review' | 'additional-docs-required' | 'approved' | 'rejected' | 'ready-for-collection';
  embassy: string;
  estimatedCompletionDate: string;
  urgencyLevel: 'low' | 'medium' | 'high' | 'critical';
  travelDate: string;
}

interface StatusAlert {
  id: string;
  applicationId: string;
  type: 'status_update' | 'document_request' | 'approval' | 'delay' | 'urgent_action';
  title: string;
  message: string;
  timestamp: string;
  priority: 'high' | 'medium' | 'low';
  read: boolean;
}

export default function VisaTrackingInteractive() {
  const [applications, setApplications] = useState<VisaApplication[]>([
    {
      id: 'visa-001',
      applicationType: 'Tourist Visa',
      destination: 'United States',
      nationality: 'United Kingdom',
      applicationNumber: 'US-2026-789456',
      submittedDate: '2026-01-15',
      currentStatus: 'under-review',
      embassy: 'US Embassy London',
      estimatedCompletionDate: '2026-02-15',
      urgencyLevel: 'high',
      travelDate: '2026-02-20'
    },
    {
      id: 'visa-002',
      applicationType: 'Business Visa',
      destination: 'India',
      nationality: 'United Kingdom',
      applicationNumber: 'IN-2026-123789',
      submittedDate: '2026-01-20',
      currentStatus: 'additional-docs-required',
      embassy: 'Indian High Commission London',
      estimatedCompletionDate: '2026-02-10',
      urgencyLevel: 'critical',
      travelDate: '2026-02-12'
    },
    {
      id: 'visa-003',
      applicationType: 'Work Visa',
      destination: 'Australia',
      nationality: 'United Kingdom',
      applicationNumber: 'AU-2026-456123',
      submittedDate: '2026-01-10',
      currentStatus: 'approved',
      embassy: 'Australian High Commission London',
      estimatedCompletionDate: '2026-02-05',
      urgencyLevel: 'low',
      travelDate: '2026-03-01'
    }
  ]);

  const [alerts, setAlerts] = useState<StatusAlert[]>([
    {
      id: 'alert-001',
      applicationId: 'visa-002',
      type: 'document_request',
      title: 'Additional Documents Required',
      message: 'Indian High Commission requires updated bank statements (last 6 months) and employment letter. Submit within 48 hours to avoid delays.',
      timestamp: '2 hours ago',
      priority: 'high',
      read: false
    },
    {
      id: 'alert-002',
      applicationId: 'visa-001',
      type: 'status_update',
      title: 'Application Under Review',
      message: 'Your US visa application has moved to interview scheduling stage. Check your email for appointment details.',
      timestamp: '5 hours ago',
      priority: 'medium',
      read: false
    },
    {
      id: 'alert-003',
      applicationId: 'visa-003',
      type: 'approval',
      title: 'Visa Approved!',
      message: 'Congratulations! Your Australian work visa has been approved. Collect your passport from the High Commission.',
      timestamp: '1 day ago',
      priority: 'high',
      read: true
    }
  ]);

  const [selectedApplication, setSelectedApplication] = useState<VisaApplication | null>(applications[0]);
  const [showAlerts, setShowAlerts] = useState(true);

  const unreadAlertsCount = alerts.filter(a => !a.read).length;

  const handleMarkAlertAsRead = (alertId: string) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, read: true } : alert
    ));
  };

  const handleSelectApplication = (app: VisaApplication) => {
    setSelectedApplication(app);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface via-white to-sky-blue/5 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="font-heading font-black text-4xl text-foreground mb-2">
                Visa Application Tracking
              </h1>
              <p className="text-muted-foreground text-lg">
                Monitor your visa applications with real-time embassy updates and AI-powered recommendations
              </p>
            </div>
            <button
              onClick={() => setShowAlerts(!showAlerts)}
              className="relative p-3 rounded-xl bg-white shadow-brand border-2 border-accent/20 hover:border-accent/40 transition-all duration-200"
            >
              <Icon name="BellIcon" size={24} className="text-primary" />
              {unreadAlertsCount > 0 && (
                <span className="absolute -top-1 -right-1 w-6 h-6 bg-coral text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadAlertsCount}
                </span>
              )}
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-brand border-2 border-sky-blue/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Applications</p>
                  <p className="text-2xl font-black text-foreground">{applications.length}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-sky-blue/10 flex items-center justify-center">
                  <Icon name="DocumentTextIcon" size={24} className="text-sky-blue" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-brand border-2 border-warning/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Under Review</p>
                  <p className="text-2xl font-black text-foreground">
                    {applications.filter(a => a.currentStatus === 'under-review').length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
                  <Icon name="ClockIcon" size={24} className="text-warning" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-brand border-2 border-trust-green/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Approved</p>
                  <p className="text-2xl font-black text-foreground">
                    {applications.filter(a => a.currentStatus === 'approved').length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-trust-green/10 flex items-center justify-center">
                  <Icon name="CheckCircleIcon" size={24} className="text-trust-green" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-brand border-2 border-coral/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Urgent Actions</p>
                  <p className="text-2xl font-black text-foreground">
                    {applications.filter(a => a.urgencyLevel === 'critical' || a.urgencyLevel === 'high').length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-coral/10 flex items-center justify-center">
                  <Icon name="ExclamationTriangleIcon" size={24} className="text-coral" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts Panel */}
        {showAlerts && (
          <div className="mb-8">
            <StatusAlerts 
              alerts={alerts} 
              onMarkAsRead={handleMarkAlertAsRead}
            />
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Applications List */}
          <div className="lg:col-span-1 space-y-4">
            <h2 className="font-heading font-bold text-xl text-foreground mb-4">
              Your Applications
            </h2>
            {applications.map(app => (
              <VisaApplicationCard
                key={app.id}
                application={app}
                isSelected={selectedApplication?.id === app.id}
                onClick={() => handleSelectApplication(app)}
              />
            ))}
          </div>

          {/* Right Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {selectedApplication && (
              <>
                {/* Status Timeline */}
                <VisaStatusTimeline application={selectedApplication} />

                {/* AI Urgency Analysis */}
                <UrgencyAnalysis application={selectedApplication} />

                {/* Marketplace Recommendations */}
                <MarketplaceRecommendations application={selectedApplication} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}